import { useState, useEffect } from 'react';
import { Pagination } from '../../../../../model/pagination';
import { IItemPurchaseAddEditRequest } from '../../../../../model/scp/purchase/request/purchase-item-add-edit-request';
import { IPurchaseItemResponse } from '../../../../../model/scp/purchase/response/purchase-item-response';
import { ITableResponse } from '../../../../../model/scp/purchase/response/tables-response';
import { IPurchaseItemResultResponse } from '../../../../../model/scp/purchase-item-result/response/purchase-item-result-response';
import { IImportErrorResponse } from '../../../../../model/scp/purchase-item-import/response/import-error-response';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import PurchaseAppService from '../../../../../application/platform/scp/purchase/purchase-app-service';
import PurchaseItemImportAppService from '../../../../../application/platform/scp/purchase/purchase-item-import-app-service';
import { useBaseData } from '../../../../../application/context/base-data-context';
import { useFeedback } from '../../../../../hooks/use-feedback';
import { sanitizeText } from '../../../../../utils/text';
import { hasErrors } from '../../../../../utils/validation';
import { validatePurchaseItem } from '../../context/purchase-validation';

const ITEMS_PAGE_SIZE = 50;
const EMPTY_ITEM: IItemPurchaseAddEditRequest = { basicProductiveIncentive: false, budgetConfidential: false };
const TEMPLATE_FILE_NAME = 'template_itens_compra.xlsx';

function parseDraftItems(json?: string): IPurchaseItemResponse[] {
    try {
        const items = JSON.parse(json || '[]');
        return Array.isArray(items) ? items : [];
    } catch {
        return [];
    }
}

function toMaterialOrService(typeItemId?: number | null): string {
    return typeItemId === 2 ? 'S' : 'M';
}

// Espelha os campos do formulário no formato em português esperado pela API do PNCP
function toApiFormat(item: IItemPurchaseAddEditRequest): IItemPurchaseAddEditRequest {
    return {
        numeroItem: item.number,
        materialOuServico: toMaterialOrService(item.typeItemId),
        tipoBeneficioId: item.typeBenefitId || '1',
        incentivoProdutivoBasico: item.basicProductiveIncentive || false,
        aplicabilidadeMargemPreferenciaNormal: false,
        aplicabilidadeMargemPreferenciaAdicional: false,
        inConteudoNacional: true,
        descricao: item.description || '',
        quantidade: item.quantity || 0,
        unidadeMedida: item.unitMeasure || '',
        valorUnitarioEstimado: item.unitValueEstimated || 0,
        valorTotal: item.amount || 0,
        orcamentoSigiloso: item.budgetConfidential || false,
        criterioJulgamentoId: item.criterionJudgmentId?.toString() || '1',
        itemCategoriaId: item.categoryItemId || '1',
        patrimonio: item.patrimony || false,
        codigoRegistroImobiliario: item.realEstateRegistryCode || '',
        catalogoId: 1,
        categoriaItemCatalogoId: 1,
        catalogoCodigoItem: 1,
        ...item,
    };
}

const FIELD_BY_INPUT: Record<string, keyof IItemPurchaseAddEditRequest> = {
    'select-category-item': 'categoryItemId',
    'select-criterion-judgment': 'criterionJudgmentId',
    'input-description': 'description',
    'input-patrimony': 'patrimony',
    'input-unit-measure': 'unitMeasure',
    'input-real-estate-registry-code': 'realEstateRegistryCode',
    'select-type-benefit': 'typeBenefitId',
    'select-situation-purchase-item': 'situationPurchaseItemId',
};
const CHECK_BY_INPUT: Record<string, keyof IItemPurchaseAddEditRequest> = {
    'check-basic-produtive-incentive': 'basicProductiveIncentive',
    'check-budget-confidential': 'budgetConfidential',
};

function downloadBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

// Itens da compra: em rascunho (antes de salvar) ficam em memória; depois são enviados um a um à API
export const usePurchaseItemController = (
    purchaseId: string | undefined,
    modalityId: number | null | undefined,
    setItemsPurchaseJson: (value: string) => void,
    initialItemsJson?: string
) => {
    const { showError, showSuccess, showWarning, confirmDelete, withLoading } = useFeedback();
    const baseData = useBaseData();
    const isDraft = purchaseId === undefined;
    const [itemPurchase, setItemPurchase] = useState<IItemPurchaseAddEditRequest>(EMPTY_ITEM);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [materialService, setMaterialService] = useState<ITableResponse[]>([]);
    const [benefitType, setBenefitType] = useState<ITableResponse[]>([]);
    const [judgementCriteria, setJudgementCriteria] = useState<ITableResponse[]>([]);
    const [categoryItemPurchase, setCategoryItemPurchase] = useState<ITableResponse[]>([]);
    const [purchaseItemSituation, setPurchaseItemSituation] = useState<ITableResponse[]>(baseData.purchaseItemSituation);
    const [draftItems, setDraftItems] = useState<IPurchaseItemResponse[]>(() => (isDraft ? parseDraftItems(initialItemsJson) : []));
    const [publishedItems, setPublishedItems] = useState<Pagination<IPurchaseItemResponse>>(new Pagination<IPurchaseItemResponse>());
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resultItemNumber, setResultItemNumber] = useState<number | null>(null);
    const [itemResult, setItemResult] = useState<IPurchaseItemResultResponse | null>(null);
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [importErrors, setImportErrors] = useState<IImportErrorResponse[]>([]);
    const [importSuccessCount, setImportSuccessCount] = useState(0);
    const [importTotalCount, setImportTotalCount] = useState(0);

    useEffect(() => {
        loadModalityTables();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalityId]);

    useEffect(() => {
        loadPublishedItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchaseId, page]);

    useEffect(() => {
        if (isDraft) setItemsPurchaseJson(JSON.stringify(draftItems));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftItems]);

    async function loadModalityTables() {
        try {
            const [materialServiceList, benefitTypeList, judgementCriteriaList, categoryList, situationList] = await Promise.all([
                BaseAppService.listMaterialService(modalityId),
                BaseAppService.listBenefitType(modalityId),
                BaseAppService.listJudgmentCriteria(modalityId),
                BaseAppService.listCategoryItemPurchase(modalityId),
                baseData.loaded ? Promise.resolve(baseData.purchaseItemSituation) : BaseAppService.listPurchaseItemSituation(),
            ]);
            setMaterialService(materialServiceList);
            setBenefitType(benefitTypeList);
            setJudgementCriteria(judgementCriteriaList);
            setCategoryItemPurchase(categoryList);
            setPurchaseItemSituation(situationList);
        } catch (error: any) {
            showError('Não foi possível carregar as tabelas de itens', error);
        }
    }

    async function loadPublishedItems() {
        if (isDraft) return;
        setLoading(true);
        try {
            setPublishedItems(await PurchaseAppService.listPurchaseItems({ id: Number(purchaseId), page: page + 1, pageSize: ITEMS_PAGE_SIZE }));
        } catch (error: any) {
            showError('Não foi possível carregar os itens', error);
        } finally {
            setLoading(false);
        }
    }

    function handleOnChange(e: any) {
        const { name, value, checked } = e.target;
        const checkField = CHECK_BY_INPUT[name];
        if (checkField) {
            setItemPurchase((previous) => ({ ...previous, [checkField]: checked }));
            return;
        }
        if (name === 'select-type-item') {
            setItemPurchase((previous) => ({ ...previous, typeItem: value, typeItemId: value }));
            return;
        }
        const field = FIELD_BY_INPUT[name];
        if (!field) return;
        setItemPurchase((previous) => ({ ...previous, [field]: name === 'input-description' ? sanitizeText(value) : value }));
    }

    // Valor total = quantidade x valor unitário
    function handleAmountChange(field: 'quantity' | 'unitValueEstimated', value: number) {
        setItemPurchase((previous) => {
            const next = { ...previous, [field]: value };
            return { ...next, amount: (next.quantity || 0) * (next.unitValueEstimated || 0) };
        });
    }

    function openItemDrawer(item: IItemPurchaseAddEditRequest) {
        setItemPurchase(item);
        setShowValidationErrors(false);
        setOpenDrawer(true);
    }

    function addItem() {
        if (!modalityId) {
            showError('Antes de adicionar um item é necessário selecionar a modalidade');
            return;
        }
        openItemDrawer(EMPTY_ITEM);
    }

    function editItem(item: IPurchaseItemResponse) {
        openItemDrawer({ ...item, typeItem: item.typeItemId } as IItemPurchaseAddEditRequest);
    }

    function closeDrawer() {
        setOpenDrawer(false);
    }

    function saveDraftItem(item: IItemPurchaseAddEditRequest) {
        const isNew = item.number === undefined;
        const number = isNew ? Math.max(0, ...draftItems.map((i) => i.number || 0)) + 1 : item.number;
        const draftItem = toApiFormat({ ...item, number }) as IPurchaseItemResponse;
        setDraftItems(isNew ? [...draftItems, draftItem] : draftItems.map((i) => (i.number === number ? draftItem : i)));
        showSuccess(isNew ? 'Item adicionado com sucesso!' : 'Item alterado com sucesso!');
    }

    async function savePublishedItem(item: IItemPurchaseAddEditRequest) {
        if (item.number === undefined) {
            await PurchaseAppService.addItemsPurchase(Number(purchaseId), [toApiFormat(item)]);
            showSuccess('Item adicionado com sucesso!');
        } else {
            await PurchaseAppService.updateItemPurchase(Number(purchaseId), item.number, toApiFormat(item));
            showSuccess('Item alterado com sucesso!');
        }
        await loadPublishedItems();
    }

    async function saveItem() {
        if (isSaving) return;
        if (hasErrors(validatePurchaseItem(itemPurchase))) {
            setShowValidationErrors(true);
            return;
        }
        if (isDraft) {
            saveDraftItem(itemPurchase);
            closeDrawer();
            return;
        }
        setIsSaving(true);
        try {
            await withLoading(() => savePublishedItem(itemPurchase));
            closeDrawer();
        } catch (error: any) {
            showError('Não foi possível salvar o item', error);
        } finally {
            setIsSaving(false);
        }
    }

    // Itens publicados não são excluídos pela API; no rascunho a exclusão é local
    function deleteItem(item: IPurchaseItemResponse) {
        confirmDelete(`Tem certeza que deseja excluir o item nº ${item.number}?`, () => {
            setDraftItems((items) => items.filter((i) => i.number !== item.number));
        });
    }

    async function openItemResultDrawer(itemNumber: number) {
        try {
            const result = await withLoading(() => PurchaseAppService.getPurchaseItemResult({ purchaseId: Number(purchaseId), itemNumber, resultSequence: 1 }));
            setItemResult(result);
            setResultItemNumber(itemNumber);
        } catch (error: any) {
            showError('Não foi possível carregar o resultado do item', error);
        }
    }

    function closeItemResultDrawer() {
        setResultItemNumber(null);
        setItemResult(null);
    }

    async function handleDownloadTemplate() {
        try {
            const blob = await withLoading(() => PurchaseItemImportAppService.downloadTemplate());
            downloadBlob(blob, TEMPLATE_FILE_NAME);
            showSuccess('Modelo baixado com sucesso!');
        } catch (error: any) {
            showError('Não foi possível baixar o modelo', error);
        }
    }

    function appendImportedItems(imported: IPurchaseItemResponse[]) {
        const lastNumber = Math.max(0, ...draftItems.map((item) => item.number || 0));
        const renumbered = imported.map((item, index) => toApiFormat({ ...item, number: lastNumber + index + 1 } as IItemPurchaseAddEditRequest) as IPurchaseItemResponse);
        setDraftItems([...draftItems, ...renumbered]);
    }

    async function handleUploadFile(file: File) {
        try {
            const result = await withLoading(() => PurchaseItemImportAppService.uploadFile(file));
            setOpenImportDialog(false);
            const importedCount = result.items?.length || 0;
            if (result.items && importedCount > 0) appendImportedItems(result.items);

            if (result.errors?.length) {
                setImportErrors(result.errors);
                setImportSuccessCount(importedCount);
                setImportTotalCount(result.totalItems);
                return;
            }
            if (importedCount > 0) {
                showSuccess(`${importedCount} ${importedCount === 1 ? 'item importado' : 'itens importados'} com sucesso!`);
                return;
            }
            showWarning('Nenhum item foi importado. Verifique o arquivo.');
        } catch (error: any) {
            setOpenImportDialog(false);
            showError('Não foi possível importar o arquivo', error);
        }
    }

    function openImportDialogHandler() {
        if (!modalityId) {
            showError('Antes de importar itens é necessário selecionar a modalidade');
            return;
        }
        setOpenImportDialog(true);
    }

    function closeImportErrorsDialog() {
        setImportErrors([]);
        setImportSuccessCount(0);
        setImportTotalCount(0);
    }

    return {
        isDraft,
        itemPurchase,
        openDrawer,
        isSaving,
        validationErrors: showValidationErrors ? validatePurchaseItem(itemPurchase) : {},
        materialService,
        benefitType,
        judgementCriteria,
        categoryItemPurchase,
        purchaseItemSituation,
        items: isDraft ? draftItems : publishedItems.items || [],
        rowCount: isDraft ? draftItems.length : publishedItems.totalRecords || 0,
        page,
        itemsPageSize: ITEMS_PAGE_SIZE,
        loading,
        resultItemNumber,
        itemResult,
        openImportDialog,
        importErrors,
        importSuccessCount,
        importTotalCount,
        handleOnChange,
        handleAmountChange,
        handlePageChange: setPage,
        addItem,
        editItem,
        closeDrawer,
        saveItem,
        deleteItem,
        openItemResultDrawer,
        closeItemResultDrawer,
        reloadItems: loadPublishedItems,
        handleDownloadTemplate,
        handleUploadFile,
        openImportDialogHandler,
        closeImportDialog: () => setOpenImportDialog(false),
        closeImportErrorsDialog,
    };
};
